import { Plus, X, MessageSquare, ChevronLeft, ChevronRight, History, Menu } from 'lucide-react';
import type { Conversation } from '../../types';
import { ConversationList } from './ConversationList';

interface ConversationSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  onToggleSidebar: () => void;
  sidebarOpen: boolean;
  isMinimized: boolean;
  onToggleMinimize: () => void;
  conversations: Conversation[];
  selectedId?: string;
  onSelectConversation: (id: string) => void;
  onNewChat: () => void;
  onDeleteConversation: (id: string) => void;
  isLoading?: boolean;
  error?: string | null;
}

function LoadingState() {
  return (
    <div className="flex items-center justify-center p-8">
      <div className="text-sm text-gray-500">Loading conversations...</div>
    </div>
  );
}

function ErrorState({ message }: { message: string }) {
  return (
    <div className="flex items-center justify-center p-8">
      <div className="text-sm text-red-500">{message}</div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center p-8 text-center">
      <MessageSquare className="h-12 w-12 text-gray-300 mb-3" />
      <p className="text-sm text-gray-500 mb-1">No conversations yet</p>
      <p className="text-xs text-gray-400">Start a new chat to begin</p>
    </div>
  );
}

export function ConversationSidebar({
  isOpen,
  onClose,
  onToggleSidebar,
  sidebarOpen,
  isMinimized,
  onToggleMinimize,
  conversations,
  selectedId,
  onSelectConversation,
  onNewChat,
  onDeleteConversation,
  isLoading = false,
  error = null,
}: ConversationSidebarProps) {
  // On mobile, don't show minimized state - just closed/open
  const showMinimized = isMinimized && isOpen;

  return (
    <>
      {/* Mobile backdrop */}
      {isOpen && !isMinimized && (
        <div
          className="fixed inset-0 bg-black/30 z-30 md:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed top-0 left-0 h-full z-40 bg-nova-color border-r border-gray-200
          md:relative md:translate-x-0
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
          ${showMinimized ? 'w-16' : 'w-80'}
          transition-all duration-300 ease-in-out
          flex flex-col
        `}
      >
        {showMinimized ? (
          // Minimized view - just icons
          <>
            {/* Minimized Header */}
            <div className="flex flex-col items-center p-2 border-b border-gray-200 gap-2">
              <button
                onClick={onToggleMinimize}
                className="p-2 hover:bg-gray-100 rounded transition-colors"
                title="Expand sidebar"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>

            {/* Minimized Icons */}
            <div className="flex-1 flex flex-col items-center py-4 gap-3">
              <button
                onClick={onNewChat}
                className="p-2 hover:bg-gray-100 rounded transition-colors"
                title="New Chat"
              >
                <Plus className="h-5 w-5" />
              </button>
              <button
                onClick={() => onToggleMinimize()}
                className="p-2 hover:bg-gray-100 rounded transition-colors relative"
                title="View History"
              >
                <History className="h-5 w-5" />
                {conversations.length > 0 && (
                  <span className="absolute -top-1 -right-1 bg-blue-600 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
                    {conversations.length > 9 ? '9+' : conversations.length}
                  </span>
                )}
              </button>
            </div>
          </>
        ) : (
          // Full view
          <>
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div className="flex items-center gap-3">
                {/* Hamburger Menu (Mobile Only) */}
                <button
                  onClick={onToggleSidebar}
                  className="md:hidden p-1 hover:bg-gray-100 rounded transition-colors"
                  aria-label="Toggle sidebar"
                >
                  {sidebarOpen ? (
                    <X className="h-6 w-6" />
                  ) : (
                    <Menu className="h-6 w-6" />
                  )}
                </button>

                {/* Branding */}
                <div className="flex-1">
                  <h2 className="text-base font-semibold font-family-messina-modern">Nebula</h2>
                  <p className="text-xs text-gray-500 font-family-messina-sans-regular">
                    Music Analytics AI Assistant
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={onNewChat}
                  className="p-1.5 hover:bg-gray-100 rounded transition-colors"
                  title="New Chat"
                >
                  <Plus className="h-5 w-5" />
                </button>
                <button
                  onClick={onToggleMinimize}
                  className="hidden md:block p-1.5 hover:bg-gray-100 rounded transition-colors"
                  title="Minimize sidebar"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto">
              {isLoading && <LoadingState />}
              {error && <ErrorState message={error} />}
              {!isLoading && !error && conversations.length === 0 && <EmptyState />}
              {!isLoading && !error && conversations.length > 0 && (
                <ConversationList
                  conversations={conversations}
                  selectedId={selectedId}
                  onSelect={onSelectConversation}
                  onDelete={onDeleteConversation}
                />
              )}
            </div>
          </>
        )}
      </aside>
    </>
  );
}
